<template>
  <component
    :is="inline ? 'span' : 'p'"
    :class="classes"
    :style="{ color: colorStyle }"
  >
    <span
      v-if="title"
      class="title"
    >
      {{ title }}
    </span>
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type ColorType =
  | 'light'
  | 'dark'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'yellow'
  | 'gray'
  | 'blue'
  | 'red'
  | 'amber'
  | 'green'
  | 'teal'
  | 'purple'
  | 'pink';

type BaseShade = '100' | '200' | '300' | '400';
type ExtendedShade = '500' | '600' | '700' | '800' | '900';

/**
 * Typography wrapper component with color variants and text styling
 *
 * Renders text as paragraph or inline span with design system colors,
 * sizes, weights, and optional title prefix. Supports complex color/shade
 * combinations with smart defaults for optimal contrast.
 *
 * @example Basic paragraph text
 * ```vue
 * <BaseText>This is a standard paragraph with default styling.</BaseText>
 * ```
 *
 * @example Inline text with color
 * ```vue
 * <BaseText inline color="error">
 *   This error message appears inline with red color (shade 300).
 * </BaseText>
 * ```
 *
 * @example Text with title prefix
 * ```vue
 * <BaseText title="Important:">
 *   The title appears bold with spacing before the content.
 * </BaseText>
 * ```
 *
 * @example Size and weight variants
 * ```vue
 * <BaseText size="lg" weight="bold">
 *   Large bold text for emphasis
 * </BaseText>
 * <BaseText size="sm" weight="light" color="neutral" shade="600">
 *   Small light text with custom shade
 * </BaseText>
 * ```
 *
 * @example Flush text (no bottom margin)
 * ```vue
 * <BaseText flush>
 *   Text without bottom margin, useful when last child
 * </BaseText>
 * ```
 */
interface BaseTextPropsBase {
  /**
   * Renders as inline span instead of paragraph block
   * @default false
   */
  inline?: boolean;

  /**
   * Text size from design system scale
   * @default undefined (uses browser default)
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Font weight from design system weights
   * @default 'normal'
   */
  weight?: 'light' | 'normal' | 'semi' | 'bold';

  /**
   * Text alignment
   * @default undefined
   */
  align?: 'center' | 'end' | 'start';

  /**
   * Removes bottom margin (useful when text is last child)
   * @default false
   */
  flush?: boolean;

  /**
   * Optional bold title prefix displayed before content
   * @default undefined
   */
  title?: string;
}

export type BaseTextProps = BaseTextPropsBase &
  (
    | {
      /**
       * Text color from design system (semantic or brand colors)
       * Supports shades 100-400 with automatic default of 300
       * @default undefined (uses default text color)
       */
      color?: ColorType;
      /**
       * Color shade intensity (100-400)
       * If not specified, defaults to 300 for better contrast
       * @default 300
       */
      shade?: BaseShade;
    }
    | {
      /**
       * Neutral gray color with extended shade range
       * Supports shades 100-900 with automatic default of 600
       * @default undefined
       */
      color?: 'neutral';
      /**
       * Neutral color shade intensity (100-900)
       * If not specified, defaults to 600 for better contrast
       * @default 600
       */
      shade?: BaseShade | ExtendedShade;
    }
    | { color?: undefined; shade?: never }
  );

const props = withDefaults(defineProps<BaseTextProps>(), {
  inline: false,
  weight: 'normal',
  flush: false,
});

/**
 * Determines shade value based on color type
 * Uses explicit shade if provided, otherwise defaults to 600 for neutral
 * and 300 for other semantic colors
 */
const actualShade = computed(() => {
  if (props.shade !== undefined) return props.shade;
  return props.color === 'neutral' ? '600' : '300';
});

const colorStyle = computed(() => {
  if (!props.color) return;

  // Standalone colors without shade variants
  if (props.color === 'light' || props.color === 'dark') {
    return `var(--color-${props.color})`;
  }

  // Colors with shade system
  return `var(--color-${props.color}-${actualShade.value})`;
});

const classes = computed(() => [
  'base-text',
  props.size && `size-${props.size}`,
  props.weight && `weight-${props.weight}`,
  props.align && `align-${props.align}`,
  { flush: props.flush },
]);
</script>

<style>
.base-text {
  font-family: var(--font-body);
  font-weight: var(--font-normal);

  &:not(span) {
    margin-bottom: var(--gap-sm);
    line-height: 1.5;
  }

  &:is(.flush, :last-child) {
    margin-bottom: 0;
  }

  &.align-center {
    text-align: center;
  }

  &.align-end {
    text-align: right;
  }

  &.weight-light {
    font-weight: var(--font-light);
  }

  &.weight-semi {
    font-weight: var(--font-semi);
  }

  &.weight-bold {
    font-weight: var(--font-bold);
  }

  &.size-xs {
    font-size: var(--font-xs);
  }

  &.size-sm {
    font-size: var(--font-md);
  }

  &.size-md {
    font-size: var(--font-md);
  }

  &.size-lg {
    font-size: var(--font-lg);
  }

  &.size-xl {
    font-size: var(--font-xl);
  }

  .title {
    font-weight: var(--font-bold);
    margin-right: var(--gap-xs);
  }
}
</style>
